// src/pages/admin/RolesAdminPage.jsx
import { useEffect, useState } from "react";
import { AdminRbacAPI } from "../../api/adminRbac";

export default function RolesAdmin() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermKeys, setRolePermKeys] = useState([]);
  const [rolePermsLoading, setRolePermsLoading] = useState(false);
  const [rolePermsError, setRolePermsError] = useState("");
  const [rolePermsSaving, setRolePermsSaving] = useState(false);

  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDescription, setNewRoleDescription] = useState("");
  const [roleSaving, setRoleSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadAll() {
      setLoading(true);
      setError("");
      try {
        const [rolesData, permsData] = await Promise.all([
          AdminRbacAPI.listRoles(),
          AdminRbacAPI.listPermissions(),
        ]);

        if (!cancelled) {
          setRoles(rolesData);
          setPermissions(permsData);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled)
          setError(err.message || "Failed to load roles/permissions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadAll();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshRoles = async () => {
    try {
      const data = await AdminRbacAPI.listRoles();
      setRoles(data);
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to refresh roles");
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!newRoleName.trim()) return;

    setRoleSaving(true);
    try {
      await AdminRbacAPI.createRole({
        name: newRoleName.trim(),
        description: newRoleDescription || undefined,
      });
      setNewRoleName("");
      setNewRoleDescription("");
      await refreshRoles();
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to create role");
    } finally {
      setRoleSaving(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (role.is_system) {
      alert("System roles cannot be deleted.");
      return;
    }
    if (!window.confirm(`Delete role "${role.name}"?`)) return;

    try {
      await AdminRbacAPI.deleteRole(role.id);
      await refreshRoles();

      if (selectedRole && selectedRole.id === role.id) {
        setSelectedRole(null);
        setRolePermKeys([]);
      }
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to delete role");
    }
  };

  const openRolePermissions = async (role) => {
    setSelectedRole(role);
    setRolePermsLoading(true);
    setRolePermsError("");
    setRolePermKeys([]);

    try {
      const rolePerms = await AdminRbacAPI.getRolePermissions(role.id);
      setRolePermKeys(rolePerms.map((p) => p.key));
    } catch (err) {
      console.error(err);
      setRolePermsError(err.message || "Failed to load role permissions");
    } finally {
      setRolePermsLoading(false);
    }
  };

  const togglePerm = (key) => {
    setRolePermKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSaveRolePermissions = async (e) => {
    e.preventDefault();
    if (!selectedRole) return;
    setRolePermsSaving(true);
    setRolePermsError("");

    try {
      await AdminRbacAPI.setRolePermissions(selectedRole.id, rolePermKeys);
      setRolePermsSaving(false);
    } catch (err) {
      console.error(err);
      setRolePermsError(err.message || "Failed to update role permissions");
      setRolePermsSaving(false);
    }
  };

  if (loading) return <p>Loading roles…</p>;
  if (error && !selectedRole) {
    return <p className="text-red-600">{error}</p>;
  }

  return (
    <div className="max-w-6xl mx-auto grid gap-6 md:grid-cols-2">
      {/* LEFT: Roles list + create */}
      <div>
        <h1 className="text-2xl font-bold mb-2">Roles & Permissions</h1>
        <p className="text-sm text-gray-600 mb-4">
          Define roles and assign permissions. Users can then be assigned
          multiple roles via the Users admin page.
        </p>

        <div className="border rounded mb-4">
          <div className="border-b px-3 py-2 bg-gray-50 flex items-center justify-between">
            <span className="font-semibold text-sm">Roles</span>
            <button
              onClick={refreshRoles}
              className="text-xs border rounded px-2 py-1 hover:bg-gray-100"
            >
              Refresh
            </button>
          </div>

          <ul className="max-h-72 overflow-y-auto divide-y text-sm">
            {roles.map((role) => (
              <li
                key={role.id}
                className={`px-3 py-2 flex items-center justify-between cursor-pointer ${
                  selectedRole && selectedRole.id === role.id
                    ? "bg-gray-100"
                    : ""
                }`}
              >
                <div
                  className="flex-1"
                  onClick={() => openRolePermissions(role)}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{role.name}</span>
                    {role.is_system && (
                      <span className="text-[10px] uppercase text-gray-400">
                        system
                      </span>
                    )}
                  </div>
                  {role.description && (
                    <p className="text-xs text-gray-500">
                      {role.description}
                    </p>
                  )}
                </div>
                {!role.is_system && (
                  <button
                    onClick={() => handleDeleteRole(role)}
                    className="ml-2 text-xs text-red-700 border border-red-300 rounded px-2 py-1 hover:bg-red-50"
                  >
                    Delete
                  </button>
                )}
              </li>
            ))}

            {roles.length === 0 && (
              <li className="px-3 py-4 text-center text-gray-500">
                No roles defined.
              </li>
            )}
          </ul>
        </div>

        <form onSubmit={handleCreateRole} className="border rounded p-3 space-y-2">
          <h2 className="text-sm font-semibold">Create new role</h2>
          <div>
            <label className="block text-xs font-medium mb-1">Name</label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1 text-sm"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">
              Description (optional)
            </label>
            <textarea
              className="w-full border rounded px-2 py-1 text-sm"
              rows={2}
              value={newRoleDescription}
              onChange={(e) => setNewRoleDescription(e.target.value)}
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              className="px-3 py-1 text-sm border rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
              disabled={roleSaving}
            >
              {roleSaving ? "Creating…" : "Create role"}
            </button>
          </div>
        </form>
      </div>

      {/* RIGHT: Permissions for selected role */}
      <div>
        <h2 className="text-lg font-semibold mb-2">
          Permissions for selected role
        </h2>

        {!selectedRole ? (
          <p className="text-sm text-gray-600">
            Select a role on the left to view and edit its permissions.
          </p>
        ) : rolePermsLoading ? (
          <p>Loading permissions…</p>
        ) : (
          <form
            onSubmit={handleSaveRolePermissions}
            className="border rounded p-3 max-h-[75vh] overflow-y-auto"
          >
            <div className="mb-3">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{selectedRole.name}</span>
                {selectedRole.is_system && (
                  <span className="text-[10px] uppercase text-gray-400">
                    system
                  </span>
                )}
              </div>
              {selectedRole.description && (
                <p className="text-xs text-gray-500">
                  {selectedRole.description}
                </p>
              )}
            </div>

            {rolePermsError && (
              <p className="text-sm text-red-600 mb-2">{rolePermsError}</p>
            )}

            <div className="border rounded p-2 max-h-[55vh] overflow-y-auto space-y-1 text-sm">
              {permissions.length === 0 && (
                <p className="text-gray-500 text-sm">
                  No permissions defined in database.
                </p>
              )}

              {permissions.map((perm) => (
                <label
                  key={perm.id}
                  className="flex items-start gap-2 py-1 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={rolePermKeys.includes(perm.key)}
                    onChange={() => togglePerm(perm.key)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-mono text-xs">{perm.key}</div>
                    {perm.description && (
                      <div className="text-xs text-gray-500">
                        {perm.description}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-3">
              <button
                type="submit"
                className="px-3 py-1 text-sm border rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                disabled={rolePermsSaving}
              >
                {rolePermsSaving ? "Saving…" : "Save permissions"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
